/**
 * Customer API Types
 * Types matching the exact structure of customer API endpoints
 * Extends base types from common.ts
 */

import type {
  ListResponse,
  PaginationParams,
  CategoryInfo,
  InternalContact,
  BaseContactResponse,
  BaseContactDetailResponse,
  BaseLocationResponse,
  BaseCreateContactRequest,
  BaseCreateLocationRequest,
  ToggleStatusResponse,
  BaseContactListParams,
  ExistingCompanyData,
} from './common';

// Re-export ExistingCompanyData for backward compatibility
export type { ExistingCompanyData } from './common';

/**
 * Customer type enumeration (matches API: individual boolean)
 */
export type CustomerType = 'Company' | 'Individual';

/**
 * Hold status enumeration (matches API holdStatus field)
 * auto = Auto (automatic status)
 * on_hold = On Hold (trading suspended)
 * off_hold = Off Hold (trading resumed)
 */
export type HoldStatus = 'auto' | 'on_hold' | 'off_hold';

/**
 * Hold status string to number mapping
 * Backend API expects integer values for holdStatus (1-based)
 * 1 = Auto, 2 = On Hold, 3 = Off Hold
 */
export const HOLD_STATUS_VALUES: Record<HoldStatus, number> = {
  auto: 1,
  on_hold: 2,
  off_hold: 3,
};

/**
 * Convert hold status string to number for API requests
 * @param status - Hold status string value
 * @returns Numeric value for API (1-3, 1-based)
 */
export function holdStatusToNumber(status: HoldStatus): number {
  return HOLD_STATUS_VALUES[status] ?? 1;
}

/**
 * Number to hold status string mapping (reverse of HOLD_STATUS_VALUES, 1-based)
 */
export const NUMBER_TO_HOLD_STATUS: Record<number, HoldStatus> = {
  1: 'auto',
  2: 'on_hold',
  3: 'off_hold',
};

/**
 * Convert hold status number from API response to string enum
 * @param value - Numeric value from API (1-3, 1-based)
 * @returns Hold status string enum
 */
export function numberToHoldStatus(value: number | null | undefined): HoldStatus {
  if (value === null || value === undefined) return 'auto';
  return NUMBER_TO_HOLD_STATUS[value] ?? 'auto';
}

/**
 * Hold status i18n key mapping for display
 * Use with t() function: t(HOLD_STATUS_I18N_KEYS[holdStatus])
 */
export const HOLD_STATUS_I18N_KEYS: Record<HoldStatus, string> = {
  auto: 'customer.holdStatus.auto',
  on_hold: 'customer.holdStatus.onHold',
  off_hold: 'customer.holdStatus.offHold',
};

/**
 * Get hold status i18n key from number value
 * Utility to convert API number directly to i18n key
 * @param value - Numeric value from API (1-3)
 * @returns i18n key string for translation
 */
export function getHoldStatusI18nKey(value: number | null | undefined): string {
  const holdStatus = numberToHoldStatus(value);
  return HOLD_STATUS_I18N_KEYS[holdStatus];
}

/**
 * Check if hold status indicates a hold (on_hold status)
 * Utility for UI styling decisions
 * @param value - Numeric value from API (1-3)
 * @returns true if status is 'on_hold'
 */
export function hasHoldStatus(value: number | null | undefined): boolean {
  return numberToHoldStatus(value) === 'on_hold';
}

/**
 * Customer currency balance information
 */
export interface CustomerCurrencyBalance {
  currency: string; // 3-letter currency code (AUD, TWD, USD, etc.)
  balance: number;
  unbilledAmount: number;
}

/**
 * Days overdue invoice information
 */
export interface DaysOverdueInvoice {
  dueDate: string; // ISO date string
  invoiceNumber: string;
  outstanding: number;
  currency: string;
}

// ============================================================================
// CUSTOMER PAYMENT TYPES
// ============================================================================

/**
 * Due date base type for payment terms
 */
export type DueDateBase = 'invoice_date' | 'delivery_date' | 'eom' | 'receipt_date';

/**
 * Customer custom payment term information (from API response)
 * Contains the full payment term details for a customer
 * Field names use "custom" prefix to match API response (same as VendorCustomPaymentTerm)
 */
export interface CustomerPaymentTermInfo {
  basePaymentTermId: number | null; // NULL = completely custom term
  termCode: string;
  customTermName: string; // Custom name or template name
  customDueDateBase: DueDateBase;
  customDueDays: number;
  customHasDiscount: boolean;
  customDiscountRate: number; // 0-100
  customDiscountDays: number;
  customDescription: string;
}

/**
 * @deprecated Use CustomerPaymentTermInfo instead
 * Agreed payment terms information (from API) - kept for backward compatibility
 */
export interface AgreedTermsInfo {
  termId: number;
  termCode: string;
  termName: string;
}

/**
 * Tax type information (from API)
 */
export interface TaxTypeInfo {
  taxTypeId: number;
  taxCode: string;
  taxName: string;
  taxRate: number;
}

/**
 * Parent/Subsidiary company reference
 */
export interface CompanyReference {
  companyId: number;
  companyName: string;
}

/**
 * Related organisation reference
 */
export interface RelatedOrganisation extends CompanyReference {
  relationType: string;
}

/**
 * Default location reference
 */
export interface DefaultLocationReference {
  locationId: number;
  locationName: string;
}

// ============================================================================
// CUSTOMER CONTACT TYPES (extends base)
// ============================================================================

/**
 * Customer contact response (extends base contact response)
 */
export type CustomerContactResponse = BaseContactResponse;

/**
 * Customer contact detail response (extends base contact detail response)
 */
export type CustomerContactDetailResponse = BaseContactDetailResponse;

// ============================================================================
// CUSTOMER LOCATION TYPES (extends base with customer-specific fields)
// ============================================================================

/**
 * Customer location response (extends base with shipping and billing flags)
 * Matches GET /api/customers/:customerId/locations response
 */
export interface CustomerLocationResponse extends BaseLocationResponse {
  customerId: number;
  isDefaultShipping: boolean;
  isDefaultBilling: boolean;
  /** board2-#79/#101: backend address-parse status ('parsed' | 'pending' | 'failed'); additive, absent on legacy rows */
  parseStatus?: 'parsed' | 'pending' | 'failed';
}

// ============================================================================
// CUSTOMER LIST TYPES
// ============================================================================

/**
 * Customer list item (summary view)
 * Matches GET /api/customers response data array
 */
export interface CustomerListItem {
  customerId: number;
  customerName: string;
  alternativeName?: string | null; // Alternative name for identification
  nickname?: string | null; // Nickname/short name for identification
  customerCode: string; // Customer code (unique identifier)
  individual: boolean; // true = Individual, false = Company
  categories: CategoryInfo[]; // Array of category objects with categoryId and category name
  localTaxId: string; // 統編/稅號 (displayed as ABN in UI)
  address: string;
  phoneNumber: string; // API returns phoneNumber, not phone
  internalContact: InternalContact | null;
  isActive: boolean;
  createdAt?: string;
  updatedAt?: string;
}

/**
 * GET /api/customers response
 */
export type CustomerListResponse = ListResponse<CustomerListItem>;

// ============================================================================
// CUSTOMER DETAIL TYPES
// ============================================================================

/**
 * Customer detail (full information)
 * Matches GET /api/customers/:customerId response
 */
export interface CustomerDetailResponse {
  // Basic identification
  customerId: number;
  name: string; // API uses 'name' not 'customerName'
  alternativeName: string | null;
  nickname: string | null;
  individual: boolean; // true = Individual, false = Company
  customerCode: string;
  localTaxId: string; // 統編/稅號

  // Categories
  categories: CategoryInfo[];

  // Internal contact (Sales Rep)
  internalContact: InternalContact | null;

  // Relationships
  parentCompany: CompanyReference | null;
  defaultLocation: DefaultLocationReference | null;
  subsidiaries: CompanyReference[];
  relatedOrganisations: RelatedOrganisation[];

  // Online account
  isActiveOnlineAccount: boolean;

  // Contact Details (main entity)
  email: string | null;
  fax: string | null;
  phoneCountryCode: string | null;
  phoneNumber: string | null; // API changed from 'phone' to 'phoneNumber' (2025/11/28)
  phoneExtension: string | null;
  web: string | null;

  // Notes
  accountNotes: string | null;
  deliveryNotes: string | null;
  salesNotes: string | null;

  // Address
  countryId: number;
  stateId: number | null; // board2-#79: backend-parse OUTPUT (Google split of the single address)
  cityId: number | null; // board2-#79: backend-parse OUTPUT
  postcode: string | null;
  address: string | null;
  addressLang: string | null;
  /**
   * board2-#79 (page 2cb89adb-b34d-80ee-a729-d094a38c45cc): status of the
   * backend address parse — 'parsed' when Google resolved stateId/cityId
   * from the single-textfield address, 'pending' when input is empty / not
   * yet parsed, 'failed' when non-empty input resolved nothing (#101 BE-03 —
   * renders a visible warning Tag next to the raw address).
   * Additive; absent on legacy rows (renderDash-tolerant consumers show '-').
   */
  parseStatus?: 'parsed' | 'pending' | 'failed';

  // Status
  isActive: boolean;

  // Timestamps
  createdAt?: string;
  updatedAt?: string;
}

// ============================================================================
// CUSTOMER CREATE/UPDATE TYPES
// ============================================================================

/**
 * POST /api/customers request body
 * Matches API spec for creating a new customer
 */
export interface CreateCustomerRequest {
  // Basic identification (required)
  name: string; // Max 255
  individual: boolean; // true = Individual, false = Company
  customerCode: string; // Max 50, must be unique
  localTaxId: string; // Max 50, 統編/稅號

  // Basic identification (optional)
  alternativeName?: string; // Max 255
  nickname?: string; // Max 255

  // Categories (required - at least 1)
  categoryIds: number[];

  // Internal contact (optional)
  internalContactUserId?: number;

  // Relationships (optional)
  parentCompanyId?: number;

  // Online account (optional)
  isActiveOnlineAccount?: boolean; // Default: false

  // Contact Details (optional)
  email?: string; // Max 255
  fax?: string; // Max 50
  phoneCountryCode?: string; // Max 10
  phoneNumber?: string; // Max 50, API changed from 'phone' to 'phoneNumber' (2025/11/28)
  phoneExtension?: string; // Max 20
  web?: string; // Max 255

  // Notes (optional)
  accountNotes?: string;
  deliveryNotes?: string;
  salesNotes?: string;

  // Address (optional - backend returns error for 0)
  countryId?: number;
  stateId?: number; // DEPRECATED(board2-#79): parse OUTPUT — single-address payloads no longer send it
  cityId?: number; // DEPRECATED(board2-#79): parse OUTPUT — single-address payloads no longer send it
  postcode?: string; // Max 20
  address?: string; // board2-#79: the FULL address in one textfield; backend splits via Google
  addressLang?: string; // Max 3, default: 'en'

  // Status (optional)
  isActive?: boolean; // Default: true
}

/**
 * PUT /api/customers/:customerId request body
 * Same as CreateCustomerRequest
 */
export type UpdateCustomerRequest = CreateCustomerRequest;

// ============================================================================
// CUSTOMER CHECK EXISTS TYPES
// ============================================================================

/**
 * GET /api/customers/check-exists response
 * Note: ExistingCompanyData is imported from common.ts
 */
export interface CustomerCheckExistsResponse {
  exists: boolean;
  companyExists: boolean;
  customerId?: number; // Only when exists=true
  message: string;
  company?: ExistingCompanyData; // Only when companyExists=true and exists=false
}

/**
 * Check exists query parameters
 */
export interface CustomerCheckExistsParams {
  countryId: number;
  localTaxId: string;
}

// ============================================================================
// CUSTOMER QUICK CREATE TYPES
// ============================================================================

/**
 * POST /api/customers/quick-create request body
 * Creates customer from existing company data
 */
export interface QuickCreateCustomerRequest {
  countryId: number;
  localTaxId: string;
}

/**
 * POST /api/customers/quick-create response
 */
export interface QuickCreateCustomerResponse {
  customerId: number;
}

// ============================================================================
// CUSTOMER TOGGLE/STATUS TYPES
// ============================================================================

/**
 * PATCH /api/customers/:customerId/toggle request body
 */
export interface ToggleCustomerStatusRequest {
  isActive: boolean;
}

/**
 * PATCH /api/customers/:customerId/toggle response
 */
export interface ToggleCustomerStatusResponse extends ToggleStatusResponse {
  customerId: number;
}

// ============================================================================
// CUSTOMER SORT/FILTER TYPES
// ============================================================================

/**
 * Customer sort fields (numeric as per API spec)
 * 1=customerId, 2=customerName, 3=customerCode, 4=localTaxId, 5=address, 6=phone, 7=isActive
 */
export type CustomerSortField = 1 | 2 | 3 | 4 | 5 | 6 | 7;

/**
 * Customer list query parameters
 * Matches GET /api/customers query params
 */
export interface CustomerListParams extends PaginationParams {
  search?: string; // Search customerName, alternativeName, nickname, localTaxId (partial, case-insensitive)
  categoryIds?: string; // Comma-separated IDs: "1,2,3"
  country?: string; // Format: "countryId-stateId-cityId" or "countryId-stateId" or "countryId"
  isActive?: number; // 1=active, 2=inactive, 3=all (default: 3)
  sortBy?: CustomerSortField;
  sortOrder?: 0 | 1; // Sort direction: 0=ASC, 1=DESC
}

// ============================================================================
// CUSTOMER LOCATION CRUD TYPES
// ============================================================================

/**
 * GET /api/customers/:customerId/locations response
 */
export interface CustomerLocationsResponse {
  data: CustomerLocationResponse[];
  pagination: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
}

/**
 * POST /api/customers/:customerId/locations request body
 */
export interface CreateCustomerLocationRequest extends BaseCreateLocationRequest {
  customerId: number;
  isDefaultShipping?: boolean;
  isDefaultBilling?: boolean;
}

/**
 * PUT /api/customers/:customerId/locations/:locationId request body
 */
export type UpdateCustomerLocationRequest = Omit<CreateCustomerLocationRequest, 'customerId'>;

/**
 * Toggle customer location status response
 */
export interface ToggleCustomerLocationStatusResponse extends ToggleStatusResponse {
  locationId: number;
}

/**
 * Set default customer location response
 */
export interface SetDefaultCustomerLocationResponse {
  message: string;
  locationId: number;
  isDefaultShipping?: boolean;
}

/**
 * Customer location list query parameters
 */
export interface CustomerLocationListParams extends PaginationParams {
  isActive?: number; // 1=active, 2=inactive, 3=all (default: 3)
}

// ============================================================================
// CUSTOMER CONTACT CRUD TYPES
// ============================================================================

/**
 * POST /api/customers/:customerId/contacts request body
 */
export interface CreateCustomerContactRequest extends BaseCreateContactRequest {
  customerId: number;
}

/**
 * PUT /api/customers/:customerId/contacts/:contactId request body
 */
export type UpdateCustomerContactRequest = Omit<CreateCustomerContactRequest, 'customerId'>;

/**
 * Toggle customer contact status response
 */
export interface ToggleCustomerContactStatusResponse extends ToggleStatusResponse {
  contactId: number;
}

/**
 * Set primary customer contact response
 */
export interface SetPrimaryCustomerContactResponse {
  message: string;
  contactId: number;
  isPrimary: boolean;
}

/**
 * Customer contact list query parameters
 */
export type CustomerContactListParams = BaseContactListParams;

// ============================================================================
// CUSTOMER PAYMENT CRUD TYPES
// ============================================================================

/**
 * GET /api/customers/:customerId/payment response
 * Matches the actual API response structure (updated 2025-12-02)
 */
export interface CustomerPaymentResponse {
  paymentId: number;
  priceLevelId: number | null;
  defaultReceivablesAccount: string | null;
  depositPercentage: number | null; // 0-100
  cashDiscountPercentage: number | null; // 0-100

  // Payment term information
  customerCustomTermId: number | null; // ID of the customer's custom term record
  paymentTerm: CustomerPaymentTermInfo | null; // Full payment term details

  dueInDays: number | null;
  creditLimit: number | null;
  holdStatus: number; // API returns number (0-4), use numberToHoldStatus() to convert to string enum
  primaryCurrency: string;

  // Tax information (flat fields, not nested object)
  taxTypeId: number | null;
  taxType: string | null; // Tax type name (e.g., "VAT", "GST")
  taxRate: number | null;

  // Tax country information
  countryId: number | null;
  countryNameEn: string | null;
  countryNameZh: string | null;

  // Invoicee and shipping
  invoiceeContactId: number | null;
  shippingInstruction: string | null;
  defaultShippingMarkFront: string | null;
  defaultShippingMarkSide: string | null;

  // Timestamps
  createdAt?: string;
  updatedAt?: string;
}

/**
 * POST /api/customers/:customerId/payment request body
 */
export interface CreateCustomerPaymentRequest {
  priceLevelId?: number;
  defaultReceivablesAccount?: string;
  depositPercentage?: number; // 0-100
  cashDiscountPercentage?: number; // 0-100
  basePaymentTermId?: number;
  customTermName?: string;
  customDueDays?: number;
  customHasDiscount?: boolean;
  customDiscountRate?: number;
  customDiscountDays?: number;
  dueInDays?: number;
  creditLimit?: number;
  holdStatus?: number; // Backend expects integer (0=none, 1=credit_hold, etc.)
  primaryCurrency: string; // Required
  taxTypeId: number; // Required
  invoiceeContactId?: number;
  shippingInstruction?: string;
  defaultShippingMarkFront?: string;
  defaultShippingMarkSide?: string;
}

/**
 * PUT /api/customers/:customerId/payment request body
 */
export type UpdateCustomerPaymentRequest = CreateCustomerPaymentRequest;

/**
 * POST/PUT /api/customers/:customerId/payment response
 */
export interface CustomerPaymentMutationResponse {
  paymentId: number;
  customerId: number;
  message: string;
}
